import React, { useEffect, useRef, useState } from 'react';
import { Box, makeStyles, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { CompanyLogo } from '../../components/CompanyLogo';
import { PoweredBy } from '../../components/PoweredBy';
import { Spinner } from '../../components/Spinner';
import { WrappedTextField } from '../../components/WrappedTextField';
import { trackButtonClicked, trackPage, useAnalyticsHomeownerParams } from '../../hooks/useEventTracking';
import { useOrgData } from '../../hooks/useOrgData';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { caZipCodeRegExp, zipCodeSuite } from '../ContactDetailsStep/validation';

import { formatCAZipCode, isZipCodeCanadian } from './utils';

const useStyles = makeStyles((theme: HCPTheme) => ({
  actionButtonsWrapper: {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    gap: `14px`,
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    zIndex: theme.zIndex.snackbar,
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    boxShadow: theme.shadows[4],
    flexDirection: 'column',
  },
  actionButtons: {
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
    width: '100%',
  },
}));

type Props = {
  children: React.ReactNode;
};

type Status = 'loading' | 'error' | 'success' | 'idle';

export const ServiceAreaWrapperV1 = ({ children }: Props) => {
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { token } = state;
  const { actionButtons, actionButtonsWrapper } = useStyles();
  const { data: orgData } = useOrgData({ token });

  const zipCodesEnabled = orgData?.zipCodesEnabled;
  const orgName = orgData?.name;
  const phoneNumber = orgData?.phoneNumber;

  const [status, setStatus] = useState<Status>('idle');
  const [lastZipCode, setLastZipCode] = useState<string>('');

  const trackingProps = useAnalyticsHomeownerParams();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    const zipCode = inputRef.current?.value || '';
    const userZipCode = isZipCodeCanadian(zipCode) ? formatCAZipCode(zipCode) : zipCode;
    const { isValid } = zipCodeSuite(userZipCode);
    trackButtonClicked({ buttonName: 'service area verify code', ...trackingProps });
    if (isValid()) {
      setStatus('loading');
      setTimeout(() => {
        const availableZipCodes = orgData?.zipCodes;
        if (availableZipCodes?.indexOf(userZipCode) !== -1) {
          dispatch(actions.setServiceAreaDefault(zipCode));
          setStatus('success');
          return;
        }

        // The user entered a long Canadian postal code, and we check whether the pro accepts the entire area (ex. K1A)
        if (caZipCodeRegExp.test(userZipCode)) {
          const shortZipCode = userZipCode.slice(0, 3);
          if (availableZipCodes?.indexOf(shortZipCode) !== -1) {
            setStatus('success');
            return;
          }
        }

        setLastZipCode(userZipCode);
        setStatus('error');
      }, 1000);
    }
  };

  const handleBackClick = () => {
    setLastZipCode('');
    setStatus('idle');
  };

  useEffect(() => {
    if (trackingProps && status === 'error') {
      trackPage('Online Booking - Service area error page', {
        ...trackingProps,
      });
    }
  }, [trackingProps, status]);

  if (!zipCodesEnabled || status === 'success') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <Box overflow="auto" maxHeight="100%" height="100%" mx={-3} px={2}>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        alignItems="center"
        justifyContent="center"
        pt={5}
        pb={8}
      >
        <Box mb={1}>
          <CompanyLogo top={false} />
        </Box>
        <Typography variant="h5" align="center" gutterBottom>
          Welcome {orgName ? `to ${orgName}` : ''}
        </Typography>
        {status === 'error' ? (
          <Typography variant="body1" align="center">
            We’re sorry we currently don’t operate in <br />
            {lastZipCode ? <b>{lastZipCode}</b> : 'entered zip code'}
            {phoneNumber ? (
              <>
                , if you have any questions <br /> please call: {phoneNumber}
              </>
            ) : (
              ''
            )}
          </Typography>
        ) : (
          <>
            <Typography variant="body1" align="center" gutterBottom>
              Let us check if we operate in your area
            </Typography>

            <Box mt={1}>
              <WrappedTextField
                onClick={handleClick}
                validation={zipCodeSuite}
                name="zipCode"
                dataTestId="online-booking-zipCode"
                label="Enter your Zip code"
                fullWidth
                inputRef={inputRef}
                autoComplete="postal-code"
              />
            </Box>
          </>
        )}
      </Box>
      <Box className={actionButtonsWrapper}>
        <Box className={actionButtons}>
          <Button
            version="v2"
            onClick={status !== 'error' ? handleClick : handleBackClick}
            size="medium"
            fullWidth
            variant="contained"
            color="secondary"
          >
            {status !== 'error' ? 'Verify zip code' : 'Back'}
          </Button>
        </Box>
        <PoweredBy />
      </Box>

      <Spinner show={status === 'loading'} />
    </Box>
  );
};
